import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const empty = {
  title: "", destination: "", region: "Domestic", country_state: "", duration: "",
  price: "", summary: "", description: "", image: "", featured: false,
  highlights: [""], tags: "", itinerary: [{ day: 1, title: "", detail: "" }],
};

export default function PackageForm({ initial, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState(
    initial
      ? {
          ...initial,
          price: String(initial.price ?? ""),
          tags: (initial.tags || []).join(", "),
          highlights: initial.highlights?.length ? initial.highlights : [""],
          itinerary: initial.itinerary?.length ? initial.itinerary : [{ day: 1, title: "", detail: "" }],
        }
      : empty
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large. Please use an image under 2MB."); return; }
    const reader = new FileReader();
    reader.onload = () => set("image", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.country_state || !form.image || !form.price) {
      toast.error("Title, destination state/country, price and image are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseInt(form.price, 10) || 0,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      highlights: form.highlights.map((h) => h.trim()).filter(Boolean),
      itinerary: form.itinerary
        .filter((d) => d.title)
        .map((d, i) => ({ day: Number(d.day) || i + 1, title: d.title, detail: d.detail || "" })),
      gallery: [],
    };
    try {
      if (editing) await api.put(`/packages/${initial.id}`, payload);
      else await api.post("/packages", payload);
      toast.success(editing ? "Package updated" : "Package created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-brand-sand bg-brand-cream px-3.5 py-2.5 text-sm text-brand-navy outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30";
  const label = "block text-xs font-semibold uppercase tracking-wider text-[#8B89A4] mb-1.5";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-navy/60 backdrop-blur-sm p-4" data-testid="package-form-modal">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl text-brand-navy">{editing ? "Edit package" : "New package"}</h2>
          <button onClick={onClose} className="text-[#8B89A4] hover:text-brand-navy" data-testid="pkg-form-close"><X size={24} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><label className={label}>Title</label><input data-testid="pf-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Region</label>
              <select className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="pf-region">
                <option>Domestic</option><option>International</option>
              </select>
            </div>
            <div><label className={label}>State / Country</label><input data-testid="pf-country" className={inputCls} value={form.country_state} onChange={(e) => set("country_state", e.target.value)} placeholder="e.g. Kerala / Thailand" /></div>
          </div>
          <div><label className={label}>Sub-locations</label><input className={inputCls} value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="Munnar · Alleppey · Kochi" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Duration</label><input className={inputCls} value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="5 Days / 4 Nights" /></div>
            <div><label className={label}>Price (₹ per person)</label><input data-testid="pf-price" type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} /></div>
          </div>
          <div>
            <label className={label}>Package image</label>
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              {form.image ? (
                <img src={form.image} alt="preview" className="h-24 w-32 rounded-lg object-cover border border-brand-sand shrink-0" data-testid="pf-image-preview" />
              ) : (
                <div className="h-24 w-32 rounded-lg bg-brand-sand grid place-items-center text-xs text-[#a3a1ba] shrink-0">No image</div>
              )}
              <div className="flex-1 w-full space-y-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-brand-navy px-4 py-2 text-xs font-semibold text-white hover:bg-brand-navydark">
                  Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={onImageFile} data-testid="pf-image-upload" />
                </label>
                <input data-testid="pf-image" className={inputCls} value={form.image?.startsWith("data:") ? "" : form.image} onChange={(e) => set("image", e.target.value)} placeholder="...or paste an image URL" />
              </div>
            </div>
          </div>
          <div><label className={label}>Summary</label><textarea rows={2} className={inputCls} value={form.summary} onChange={(e) => set("summary", e.target.value)} /></div>
          <div><label className={label}>Description</label><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div><label className={label}>Tags (comma separated)</label><input className={inputCls} value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="beach, luxury" /></div>

          {/* Highlights */}
          <div>
            <label className={label}>Highlights</label>
            <div className="space-y-2">
              {form.highlights.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inputCls} value={h} onChange={(e) => set("highlights", form.highlights.map((x, j) => (j === i ? e.target.value : x)))} />
                  <button type="button" onClick={() => set("highlights", form.highlights.filter((_, j) => j !== i))} className="text-[#8B89A4] hover:text-brand-orange px-2"><Trash size={18} /></button>
                </div>
              ))}
              <button type="button" onClick={() => set("highlights", [...form.highlights, ""])} className="inline-flex items-center gap-1.5 text-sm text-brand-teal font-medium"><Plus size={16} /> Add highlight</button>
            </div>
          </div>

          {/* Itinerary */}
          <div>
            <label className={label}>Itinerary</label>
            <div className="space-y-3">
              {form.itinerary.map((d, i) => (
                <div key={i} className="rounded-lg border border-brand-sand p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-brand-teal">Day</span>
                    <input type="number" className="w-16 rounded border border-brand-sand bg-brand-cream px-2 py-1.5 text-sm" value={d.day} onChange={(e) => set("itinerary", form.itinerary.map((x, j) => (j === i ? { ...x, day: e.target.value } : x)))} />
                    <input className={inputCls} placeholder="Day title" value={d.title} onChange={(e) => set("itinerary", form.itinerary.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                    <button type="button" onClick={() => set("itinerary", form.itinerary.filter((_, j) => j !== i))} className="text-[#8B89A4] hover:text-brand-orange"><Trash size={18} /></button>
                  </div>
                  <textarea rows={2} className={inputCls} placeholder="Details" value={d.detail} onChange={(e) => set("itinerary", form.itinerary.map((x, j) => (j === i ? { ...x, detail: e.target.value } : x)))} />
                </div>
              ))}
              <button type="button" onClick={() => set("itinerary", [...form.itinerary, { day: form.itinerary.length + 1, title: "", detail: "" }])} className="inline-flex items-center gap-1.5 text-sm text-brand-teal font-medium"><Plus size={16} /> Add day</button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-brand-navy">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pf-featured" className="h-4 w-4 accent-brand-teal" />
            Feature on homepage
          </label>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-full border border-brand-sand px-6 py-3 text-sm font-semibold text-brand-navy">Cancel</button>
            <button type="submit" disabled={saving} data-testid="pf-save" className="flex-1 rounded-full bg-brand-teal px-6 py-3 text-sm font-semibold text-white hover:bg-brand-tealdark disabled:opacity-60">{saving ? "Saving..." : "Save package"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
